class StickyAddToCart {
  constructor() {
    this.stickyBar = document.getElementById('sticky-add-to-cart');
    if (!this.stickyBar) return;

    // Try to get form ID from sticky bar elements
    this.productFormId = this.stickyBar.querySelector('[form]')?.getAttribute('form');
    
    // If not found, try to find any product form on the page
    if (!this.productFormId) {
      const productForm = document.querySelector('form[data-type="add-to-cart-form"]');
      this.productFormId = productForm?.id;
    }
    
    this.productForm = document.querySelector(`#${this.productFormId}`) || 
                      document.querySelector('form[data-type="add-to-cart-form"]');
    
    if (!this.productForm) {
      console.warn('Sticky Add to Cart: Product form not found');
      return;
    }

    this.init();
  }

  init() {
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Setup scroll listener
    this.setupScrollListener();

    // Sync quantity inputs
    this.syncQuantity();

    // Sync variant selection
    this.syncVariantSelection();

    // Handle quantity buttons in sticky bar
    this.setupQuantityButtons();

    // Handle form submission
    this.setupFormSubmission();

    // Initial price update
    this.updatePrice();
  }

  setupScrollListener() {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Find the main add to cart button
          const mainSubmitButton = this.productForm.querySelector('[type="submit"][name="add"]');
          let isMainButtonVisible = false;
          
          if (mainSubmitButton) {
            const buttonRect = mainSubmitButton.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const windowWidth = window.innerWidth;
            
            // Check if button is fully or partially visible in viewport
            isMainButtonVisible = (
              buttonRect.top >= 0 &&
              buttonRect.top <= windowHeight &&
              buttonRect.left >= 0 &&
              buttonRect.left <= windowWidth &&
              buttonRect.bottom >= 0 &&
              buttonRect.bottom <= windowHeight
            );
          }
          
          // Check if we're near the bottom of the page (within 100px)
          const scrollY = window.scrollY || window.pageYOffset;
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;
          const isNearBottom = (scrollY + windowHeight) >= (documentHeight - 100);
          
          // // Show sticky bar 
          if (!isMainButtonVisible && !isNearBottom) {
                  this.stickyBar.style.display = 'block';
                  this.stickyBar.classList.add('visible');
          } else {
          // DO NOTHING — do not hide
          this.stickyBar.style.display = 'block';
          this.stickyBar.classList.add('visible');
          }

          
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    handleScroll();
  }

  syncQuantity() {
    const mainQuantityInput = document.querySelector('quantity-input input');
    const stickyQuantityInput = document.querySelector('#sticky-quantity-input');

    if (!mainQuantityInput || !stickyQuantityInput) return;

    let isSyncing = false;
    let lastMainValue = mainQuantityInput.value;
    let lastStickyValue = stickyQuantityInput.value;

    // Get and store the ORIGINAL unit price ONCE at initialization
    const getOriginalUnitPrice = () => {
      const mainPriceElement = document.querySelector('.product__info-wrapper .price-item--regular.real-price');
      if (!mainPriceElement) return null;
      
      const priceText = mainPriceElement.textContent.trim();
      // Extract number from price text (e.g., "Rs. 1,099" -> 1099)
      const priceMatch = priceText.replace(/[^\d]/g, '');
      return parseInt(priceMatch) || null;
    };

    // Store unit price ONCE - never recalculate it
    const UNIT_PRICE = getOriginalUnitPrice();
    
    if (!UNIT_PRICE) {
      console.error('Could not get unit price');
      return;
    }

    console.log('Original Unit Price:', UNIT_PRICE);

    // Update both prices based on quantity
    const updatePrices = (quantity) => {
      const qty = parseInt(quantity) || 1;
      const totalPrice = UNIT_PRICE * qty;
      const formattedPrice = `Rs. ${totalPrice.toLocaleString('en-IN')}`;

      console.log(`Quantity: ${qty}, Total Price: ${totalPrice}, Formatted: ${formattedPrice}`);

      // Update main price
      const mainPriceElement = document.querySelector('.product__info-wrapper .price-item');
      if (mainPriceElement) {
        mainPriceElement.textContent = formattedPrice;
      }

      // Update sticky price
      const stickyPriceElement = document.querySelector('#sticky-add-to-cart .price-item');
      if (stickyPriceElement) {
        stickyPriceElement.textContent = formattedPrice;
      }
    };

    // Sync from main to sticky - only if value actually changed
    const syncToSticky = () => {
      if (isSyncing) return;
      
      const currentValue = mainQuantityInput.value;
      if (currentValue === lastMainValue || currentValue === stickyQuantityInput.value) return;
      
      isSyncing = true;
      lastMainValue = currentValue;
      stickyQuantityInput.value = currentValue;
      lastStickyValue = currentValue;
      
      // Update prices
      updatePrices(currentValue);
      
      setTimeout(() => { isSyncing = false; }, 100);
    };

    // Sync from sticky to main - only if value actually changed
    const syncToMain = () => {
      if (isSyncing) return;
      
      const currentValue = stickyQuantityInput.value;
      if (currentValue === lastStickyValue || currentValue === mainQuantityInput.value) return;
      
      isSyncing = true;
      lastStickyValue = currentValue;
      mainQuantityInput.value = currentValue;
      lastMainValue = currentValue;
      
      // Update prices
      updatePrices(currentValue);
      
      setTimeout(() => { isSyncing = false; }, 100);
    };

    // Listen only to change events (not input) to reduce event firing
    mainQuantityInput.addEventListener('change', syncToSticky);
    stickyQuantityInput.addEventListener('change', syncToMain);

    // Initial sync
    syncToSticky();
  }

  syncVariantSelection() {
    // Listen for variant changes from Shopify's variant picker
    const variantInput = this.productForm.querySelector('[name="id"]');
    if (!variantInput) return;

    const handleVariantChange = () => {
      this.updatePrice();
      this.updateAvailability();
    };

    // Use MutationObserver to watch for variant changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && (mutation.attributeName === 'value' || mutation.attributeName === 'disabled')) {
          handleVariantChange();
        }
      });
    });
    
    if (variantInput) {
      observer.observe(variantInput, { attributes: true, attributeFilter: ['value', 'disabled'] });
      variantInput.addEventListener('change', handleVariantChange);
    }

    // Listen for variant change events from Shopify's variant picker components
    document.addEventListener('variant:change', handleVariantChange);

    // Bundle product: price updates after fetch, so listen for explicit price-updated event
    document.addEventListener('bundle-price-updated', () => {
      this.updatePrice();
      this.updateAvailability();
    });

    // Watch for variant-selects or variant-radios components
    const variantSelector = document.querySelector('variant-selects') || document.querySelector('variant-radios');
    if (variantSelector) {
      variantSelector.addEventListener('change', handleVariantChange);
    }
    
    // Watch for button state changes
    const mainSubmitButton = this.productForm.querySelector('[type="submit"][name="add"]');
    if (mainSubmitButton) {
      const buttonObserver = new MutationObserver(() => {
        this.syncLoadingState();
        this.updateAvailability();
      });
      buttonObserver.observe(mainSubmitButton, { 
        attributes: true, 
        attributeFilter: ['disabled', 'class'],
        childList: true,
        subtree: true
      });
    }
    
    // Initial update
    setTimeout(() => handleVariantChange(), 100);
  }

  setupQuantityButtons() {
    // The quantity buttons are handled by the quantity-input custom element
    // The sync happens automatically through the event listeners in syncQuantity()
    // No additional handling needed here
  }

  setupFormSubmission() {
    if (Shopify.country == "IN"){
    const stickyButton = document.getElementById('sticky-add-to-cart-button');
    if (!stickyButton) return;

    stickyButton.addEventListener('click', (e) => {
      // Prevent default form submission
      e.preventDefault();

      
      // Find the main submit button and trigger it
      const mainSubmitButton = this.productForm.querySelector('[type="submit"][name="add"]');
      if (mainSubmitButton) {
        // Sync quantity before submission
        const mainQuantityInput = document.querySelector('quantity-input input');
        const stickyQuantityInput = document.querySelector('#sticky-quantity-input');
        if (mainQuantityInput && stickyQuantityInput) {
          mainQuantityInput.value = stickyQuantityInput.value;
        }
        
        // Sync loading state
        this.syncLoadingState();
        
        // Trigger the main form submission
        mainSubmitButton.click();
      }
    });

    // Listen for form submission events to sync loading state
    this.productForm.addEventListener('submit', () => {
      this.syncLoadingState();
    });
    }
  }

  syncLoadingState() {
    const mainSubmitButton = this.productForm.querySelector('[type="submit"][name="add"]');
    const stickyButton = document.getElementById('sticky-add-to-cart-button');
    const stickySpinner = stickyButton?.querySelector('.loading-overlay__spinner');
    const mainSpinner = mainSubmitButton?.querySelector('.loading-overlay__spinner');
    
    if (!mainSubmitButton || !stickyButton) return;

    // Check if main button is loading
    const isLoading = mainSubmitButton.classList.contains('loading');
    
    if (isLoading) {
      stickyButton.classList.add('loading');
      stickyButton.setAttribute('aria-disabled', 'true');
      stickyButton.disabled = true;
      if (stickySpinner) stickySpinner.classList.remove('hidden');
    } else {
      stickyButton.classList.remove('loading');
      stickyButton.removeAttribute('aria-disabled');
      stickyButton.disabled = false;
      if (stickySpinner) stickySpinner.classList.add('hidden');
    }
    
    // Also observe the main button for loading class changes
    if (mainSubmitButton && !this.loadingObserver) {
      this.loadingObserver = new MutationObserver(() => {
        const isLoading = mainSubmitButton.classList.contains('loading');
        if (isLoading) {
          stickyButton.classList.add('loading');
          if (stickySpinner) stickySpinner.classList.remove('hidden');
        } else {
          stickyButton.classList.remove('loading');
          if (stickySpinner) stickySpinner.classList.add('hidden');
        }
      });
      
      this.loadingObserver.observe(mainSubmitButton, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
  }

  updatePrice() {
    const priceElement = document.getElementById('sticky-price');
    if (!priceElement) return;

    // Find the main price element
    const section = this.productForm.closest('.section');
    const sectionId = section?.id ? section.id.split('-').pop() : '';
    let mainPriceElement = document.querySelector(`#price-${sectionId}`);
    
    if (!mainPriceElement) {
      mainPriceElement = document.querySelector('.pricecontainer');
    }

    if (mainPriceElement) {
      // Extract only the actual price value (not badges, compare-at, or discount percentage)
      // Check if there's a sale price (the actual selling price)
      let priceToShow = mainPriceElement.querySelector('.price-item--sale.price-item--last');
      
      // If no sale price, get the regular price (but not the strikethrough compare-at price)
      if (!priceToShow) {
        priceToShow = mainPriceElement.querySelector('.price-item--regular.real-price');
      }
      
      if (priceToShow) {
        // Get just the price text
        const priceText = priceToShow.textContent.trim();
        // Update sticky price to show only the price value
        const priceRegularEl = priceElement.querySelector('.price-item--regular');
        if (priceRegularEl) {
          priceRegularEl.textContent = priceText;
        }
      }
    }
  }

  updateAvailability() {
    const stickyButton = document.getElementById('sticky-add-to-cart-button');
    if (!stickyButton) return;

    const variantInput = this.productForm.querySelector('[name="id"]');
    if (!variantInput) return;

    const variantId = variantInput.value;
    const mainSubmitButton = this.productForm.querySelector('[type="submit"][name="add"]');
    
    // Sync button state with main button
    if (mainSubmitButton) {
      stickyButton.disabled = mainSubmitButton.disabled;
      const mainButtonText = mainSubmitButton.querySelector('span')?.textContent.trim();
      
      if (mainButtonText) {
        stickyButton.querySelector('span').textContent = mainButtonText.toUpperCase() === 'ADD TO CART' ? 'ADD TO CART' : mainButtonText;
      } else {
        // Fallback: check variant availability
        const variantOption = this.productForm.querySelector(`option[value="${variantId}"]`);
        const isAvailable = variantOption && !variantOption.disabled && !variantOption.textContent.toLowerCase().includes('sold out');
        const isComingSoon = variantOption && variantOption.textContent.toLowerCase().includes('coming soon');

        if (isComingSoon) {
          stickyButton.disabled = true;
          stickyButton.querySelector('span').textContent = 'Coming soon';
        } else if (!isAvailable) {
          stickyButton.disabled = true;
          stickyButton.querySelector('span').textContent = 'Sold out';
        } else {
          stickyButton.disabled = false;
          stickyButton.querySelector('span').textContent = 'ADD TO CART';
        }
      }
    }
  }
}

// Initialize sticky add to cart
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StickyAddToCart();
  });
} else {
  new StickyAddToCart();
}

